export type NavigationSection = {
  id: string;
  label: string;
};

export type CareModeId = 'companion' | 'feline' | 'urgent';

export type CareMode = {
  id: CareModeId;
  label: string;
  blurb: string;
  accent: string;
  light: string;
  fog: string;
};

export type CareChapter = {
  id: string;
  title: string;
  body: string;
  petId: string;
};

export type ServiceItem = {
  title: string;
  body: string;
  petId: string;
};

export type TrustQuote = {
  name: string;
  date: string;
  quote: string;
};

export const navigationSections: NavigationSection[] = [
  { id: 'care', label: 'Consult' },
  { id: 'modes', label: 'Visit type' },
  { id: 'services', label: 'Services' },
  { id: 'companions', label: 'Pets' },
  { id: 'trust', label: 'Reviews' },
  { id: 'visit', label: 'Find us' },
];

export const careModes: CareMode[] = [
  {
    id: 'companion',
    label: 'Dogs',
    blurb: 'Steady consults and home plans for the dogs who share your couch.',
    accent: '#C4A574',
    light: '#3F8F6C',
    fog: '#1F5640',
  },
  {
    id: 'feline',
    label: 'Cats',
    blurb: 'Quieter light, unhurried exams, and cats who set the pace.',
    accent: '#8FAFBF',
    light: '#4A6F7C',
    fog: '#2A4450',
  },
  {
    id: 'urgent',
    label: 'After-hours',
    blurb: 'When something feels wrong and you need the emergency mobile now.',
    accent: '#E0A090',
    light: '#B85A45',
    fog: '#7A3428',
  },
];

export const careChapters: CareChapter[] = [
  {
    id: 'arrive',
    title: 'Arrive calm',
    body: 'You pull into Pitzer Road with a worried pet. The team hears your story first.',
    petId: 'dog-02',
  },
  {
    id: 'listen',
    title: 'Listen first',
    body: 'The consult starts with what you noticed at home, then turns findings into plain choices.',
    petId: 'cat-01',
  },
  {
    id: 'examine',
    title: 'Examine with care',
    body: 'Vaccines, diagnostics, X-rays or surgery when needed: clinical work, done kindly.',
    petId: 'dog-05',
  },
  {
    id: 'plan',
    title: 'Leave with a plan',
    body: 'You go home knowing what to watch for and when to call.',
    petId: 'cat-04',
  },
];

export const serviceItems: ServiceItem[] = [
  {
    title: 'Consults & exams',
    body: 'Sick visits and wellness checks for dogs and cats across Midrand.',
    petId: 'dog-01',
  },
  {
    title: 'Vaccines & prevention',
    body: 'Vaccinations and microchipping, plus pet food and supplies.',
    petId: 'cat-03',
  },
  {
    title: 'Surgery',
    body: 'Planned procedures with clear consent and aftercare you can follow at home.',
    petId: 'dog-08',
  },
  {
    title: 'Diagnostics & radiology',
    body: 'Diagnostics and X-rays, so treatment rests on what the tests show.',
    petId: 'cat-02',
  },
  {
    title: 'Breeding support',
    body: 'Pregnancy ultrasonography and reproductive care for owners who need specialist attention.',
    petId: 'dog-04',
  },
  {
    title: 'Acupuncture & homeopathy',
    body: 'Complementary options alongside conventional medicine when they fit the case.',
    petId: 'cat-05',
  },
];

export const trustQuotes: TrustQuote[] = [
  {
    name: 'Lara Cocciuti',
    date: 'April 2018',
    quote:
      'Friendly and very professional. The best veterinary clinic I’ve taken my pets to. They care about the animals as well as the owners.',
  },
  {
    name: 'VALOSHNI APPALSAMY',
    date: 'March 2017',
    quote:
      'The vet was so kind and loving to my little kitten. Excellent service received. Very helpful with answering my questions as a first time pet owner.',
  },
  {
    name: 'Julia Goodman',
    date: 'August 2017',
    quote: 'Natalie is a fantastic vet. Caring and considerate.',
  },
];

export const careModeLookup = Object.fromEntries(careModes.map((mode) => [mode.id, mode])) as Record<
  CareModeId,
  CareMode
>;
